import React from "react";
import { cn } from "@/lib/utils";

const STATUS_MAP = {
  active: "bg-emerald-50 text-emerald-800 border-emerald-200",
  sold: "bg-blue-50 text-blue-800 border-blue-200",
  returned: "bg-rose-50 text-rose-800 border-rose-200",
  expired: "bg-amber-50 text-amber-800 border-amber-200",
  donated: "bg-purple-50 text-purple-800 border-purple-200",
  extended: "bg-indigo-50 text-indigo-800 border-indigo-200",
  archived: "bg-slate-100 text-slate-600 border-slate-200",
  paid: "bg-emerald-100 text-emerald-900 border-emerald-300",
  pending: "bg-amber-100 text-amber-900 border-amber-300",
  approved: "bg-blue-100 text-blue-900 border-blue-300",
  draft: "bg-slate-100 text-slate-700 border-slate-300",
  completed: "bg-emerald-50 text-emerald-800 border-emerald-200",
  partially_refunded: "bg-rose-50 text-rose-800 border-rose-200",
  not_connected: "bg-slate-100 text-slate-600 border-slate-200",
  connected: "bg-emerald-100 text-emerald-900 border-emerald-300",
  pending_credentials: "bg-amber-100 text-amber-900 border-amber-300",
  inactive: "bg-slate-100 text-slate-600 border-slate-200",
  past_due: "bg-rose-50 text-rose-800 border-rose-200",
  canceled: "bg-rose-50 text-rose-800 border-rose-200",
};

export function StatusBadge({ status, className, testid }) {
  const key = (status || "").toLowerCase();
  const label = (status || "").replace(/_/g, " ");
  return (
    <span data-testid={testid}
      className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize",
        STATUS_MAP[key] || "bg-slate-100 text-slate-700 border-slate-200", className)}>
      {label}
    </span>
  );
}

export function PageHeader({ title, subtitle, children, testid }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6" data-testid={testid}>
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="text-slate-500 mt-1 text-sm">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, accent = "emerald", testid, hint }) {
  const accents = {
    emerald: "text-emerald-700 bg-emerald-50",
    amber: "text-amber-700 bg-amber-50",
    rose: "text-rose-700 bg-rose-50",
    blue: "text-blue-700 bg-blue-50",
    slate: "text-slate-700 bg-slate-100",
  };
  return (
    <div data-testid={testid}
      className="bg-white p-5 rounded-xl border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow stagger-in">
      <div className="flex items-start justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        {Icon && <span className={cn("p-1.5 rounded-lg", accents[accent])}><Icon className="w-4 h-4" /></span>}
      </div>
      <p className="font-mono-fin text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mt-2">{value}</p>
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-4">
      {Icon && <div className="p-4 rounded-2xl bg-slate-100 mb-4"><Icon className="w-8 h-8 text-slate-400" /></div>}
      <h3 className="font-display text-lg font-bold text-slate-800">{title}</h3>
      {description && <p className="text-slate-500 text-sm mt-1 max-w-sm">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Spinner({ label = "Loading…" }) {
  return (
    <div className="flex items-center justify-center py-20 text-slate-400 gap-3">
      <div className="w-5 h-5 border-2 border-slate-300 border-t-emerald-600 rounded-full animate-spin" />
      <span className="text-sm">{label}</span>
    </div>
  );
}
